import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Drawer,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { getKullaniciBildirimleri } from "../services/api";

/* ---------- COLORS ---------- */
const ORANGE = {
  50: "#FFF7ED",
  100: "#FFEDD5",
  200: "#FED7AA",
  300: "#FDBA74",
  400: "#FB923C",
  500: "#F97316",
  600: "#EA580C",
};

const VIOLET = { 500: "#ff0000", 600: "#ff0000" };
const CYAN = { 500: "#06B6D4", 600: "#0891B2" };
const GREEN = { 500: "#83d8a2", 600: "#689177" };

/* ---------- TYPE STYLES ---------- */
const typeStyles = {
  warning: { rail: ORANGE[500], dot: ORANGE[500] },
  neutral: { rail: ORANGE[300], dot: ORANGE[300] },
  info: { rail: CYAN[600], dot: CYAN[500] },
  danger: { rail: VIOLET[600], dot: VIOLET[500] },
  success: { rail: GREEN[600], dot: GREEN[500] },
};

/* ---------- TYPE PICKER (MUTLAKA COMPONENT ÜSTÜNDE) ---------- */
function pickType(b) {
  const title = (b.MESAJ_BASLIK || "").toLowerCase();
  const text = (b.BILDIRIM_METNI || "").toLowerCase();

  if (
    title.includes("düşük pil") ||
    text.includes("pili düşük") ||
    text.includes("arıza") ||
    text.includes("kesildi")
  )
    return "danger";

  if (title.includes("verisi girilmemiştir") || text.includes("girilmemiştir"))
    return "warning";

  if (title.includes("yem stoklarınız") || text.includes("tahmin edilmektedir"))
    return "info";

  return "neutral";
}
export default function NotificationDrawer({ kullaniciId, cihazTokenId, buttonSx }) {
  
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bildirimler, setBildirimler] = useState([]);

  const okunmamisSayisi = useMemo(() => {
    return bildirimler.filter(
      (b) => b.OKUNDU === "H" || b.OKUNDU === false || b.OKUNDU === 0
    ).length;
  }, [bildirimler]);

  const fetchBildirimler = useCallback(async () => {
    if (!kullaniciId || !cihazTokenId) return;

    setLoading(true);
    try {
      const res = await getKullaniciBildirimleri({ kullaniciId, cihazTokenId });

      const raw = res?.data;
      const list = raw?.data?.data ?? raw?.data ?? raw?.Data ?? raw ?? [];

      setBildirimler(Array.isArray(list) ? list : []);
    } catch (e) {
      setBildirimler([]);
    } finally {
      setLoading(false);
    }
  }, [kullaniciId, cihazTokenId]);

  const handleOpen = async () => {
    setOpen(true);
    await fetchBildirimler();
  };

  return (
    <>
      {/* BUTTON */}
      <Tooltip title="Bildirimler">
        <IconButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleOpen();
          }}
           sx={buttonSx}
        >
          <Box sx={{ position: "relative" }}>
            <NotificationsNoneIcon sx={{ fontSize: 22, color: "rgba(0,0,0,0.72)" }} />
            {okunmamisSayisi > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        minWidth: 18,
                        height: 18,
                        px: 0.6,
                        borderRadius: 999,
                        bgcolor: ORANGE[500],
                        color: "#fff",
                        fontSize: 10,
                        display: "grid",
                        placeItems: "center",
                        fontWeight: 900,
                        boxShadow: "0 6px 14px rgba(249,115,22,0.25)",
                      }}
                    >
                      {okunmamisSayisi}
                    </Box>

            )}
          </Box>
        </IconButton>
      </Tooltip>

      {/* DRAWER (Sayfanın en üstünden başlar) */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        transitionDuration={220}
        ModalProps={{
          BackdropProps: {
            sx: {
              backdropFilter: "blur(6px)",
              backgroundColor: "rgba(15, 23, 42, 0.22)",
            },
          },
        }}
        PaperProps={{
          sx: {
            width: 390,
            top: 0,          // ✅ en üst
            height: "100vh", // ✅ full
            borderTopLeftRadius: 18,
            borderBottomLeftRadius: 18,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* HEADER (soft, az turuncu) */}
        <Box
          sx={{
            p: 2,
            background: `linear-gradient(180deg, ${ORANGE[50]} 0%, #FFFFFF 100%)`,
            borderBottom: "1px solid rgba(15,23,42,0.06)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography fontWeight={950} fontSize={16} sx={{ color: "rgba(15,23,42,0.92)" }}>
                Bildirimler
              </Typography>
              
            </Box>

            <IconButton onClick={() => setOpen(false)} sx={{ color: "rgba(15,23,42,0.7)" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Button
            fullWidth
            size="small"
            sx={{
              mt: 1.4,
              bgcolor: ORANGE[100],
              color: "rgba(15,23,42,0.85)",
              fontWeight: 900,
              borderRadius: 2,
              "&:hover": { bgcolor: ORANGE[200] },
              textTransform: "none",
              border: "1px solid rgba(249,115,22,0.18)",
            }}
            onClick={() => setBildirimler([])}
          >
            Tümünü Temizle
          </Button>
        </Box>

        {/* BODY */}
        <Box sx={{ p: 2, overflowY: "auto", flex: 1, bgcolor: "#fff" }}>
          {loading ? (
            <Box sx={{ py: 6, display: "grid", placeItems: "center" }}>
              <CircularProgress size={22} />
            </Box>
          ) : bildirimler.length === 0 ? (
            <Typography variant="body2" sx={{ color: "rgba(15,23,42,0.65)" }}>
              Henüz bildirim yok.
            </Typography>
          ) : (
            <Stack spacing={1.2}>
              {bildirimler.map((b, i) => {
                const t = pickType(b);
                const s = typeStyles[t] || typeStyles.neutral;

                return (
                  <Box
                    key={i}
                    sx={{
                      position: "relative",
                      borderRadius: 3,
                      p: 1.5,
                      backgroundColor: "#fff",
                      boxShadow: "0 10px 26px rgba(15,23,42,0.06)",
                      border: "1px solid rgba(15,23,42,0.06)",
                      overflow: "hidden",
                      transition: "transform 140ms ease, box-shadow 140ms ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 14px 34px rgba(15,23,42,0.10)",
                      },
                    }}
                  >
                    {/* left rail */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 5,
                        backgroundColor: s.rail,
                        opacity: 0.9,
                      }}
                    />

                    {/* top-right dot */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: s.dot,
                        boxShadow: "0 0 0 4px rgba(249,115,22,0.10)",
                      }}
                    />

                    {/* title */}
                    <Typography
                      fontWeight={950}
                      fontSize={13}
                      sx={{ lineHeight: 1.25, pr: 3, pl: 1, color: "rgba(15,23,42,0.92)" }}
                    >
                      {b.MESAJ_BASLIK || "Bildirim"}
                    </Typography>

                    {/* message (2 line clamp) */}
                    <Typography
                      fontSize={12}
                      sx={{
                        mt: 0.6,
                        pl: 1,
                        pr: 2.5,
                        color: "rgba(15,23,42,0.72)",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {b.BILDIRIM_METNI || ""}
                    </Typography>

                    {/* footer */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 1.1,
                        pl: 1,
                      }}
                    >
                      <Typography
                        fontSize={11}
                        sx={{ color: "rgba(15,23,42,0.55)", fontWeight: 700 }}
                      >
                        {b.TARIH || "az önce"}
                      </Typography>

                      <Typography
                        fontSize={11}
                        sx={{ color: "rgba(15,23,42,0.55)", fontWeight: 800 }}
                      >
                        {b.KUMES_ADI || ""}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </Drawer>
    </>
  );
}